import React, { useState } from 'react';
import { useAuth, useUser, useClerk } from '@clerk/clerk-react';
import { ArrowLeft, Settings, Bell, Moon, Sun, Globe, Shield, Trash2, Download, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { ScrollArea } from './ui/scroll-area';
import { useUserStore } from '../../stores/userStore';
import { toast } from 'sonner';
import { LanguageOptions } from './language-toggle';
import { useI18n } from '../../i18n';

interface SettingsViewProps {
  onClose: () => void;
}

export function SettingsView({ onClose }: SettingsViewProps) {
  const { t } = useI18n();
  const { getToken } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { profile } = useUserStore();
  
  // Local settings state (would typically persist to backend)
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: false,
    darkMode: false,
    autoPlayAudio: true,
    highContrastMode: false,
  });

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSettingChange = (key: keyof typeof settings) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: !prev[key] };
      toast.success(t('settings.toast.updated'));
      return newSettings;
    });
  };

  const handleExportData = async () => {
    toast.info(t('settings.toast.exportPreparing'));
    // This would typically trigger a backend job
    setTimeout(() => {
      toast.success(t('settings.toast.exportQueued'));
    }, 1500);
  };

  const handleDeleteAccount = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    setIsDeleting(true);
    try {
      // This would call the backend to delete the account
      toast.error(t('settings.toast.deleteDisabled'));
      setShowDeleteConfirm(false);
    } catch (error) {
      toast.error(t('settings.toast.deleteFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-gradient-to-br from-white to-gray-50">
      {/* Header */}
      <div className="border-b border-white/20 bg-white/60 backdrop-blur-xl px-4 sm:px-6 py-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            aria-label={t("common.back")}
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <ArrowLeft className="h-4 w-4 text-[#434343] rtl-flip" />
          </Button>
          <h1 className="text-xl sm:text-2xl font-display text-[#434343]">{t('settings.title')}</h1>
        </div>
      </div>
                  
      <ScrollArea className="min-h-0 flex-1 p-4 sm:p-6">
        <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
          
          {/* Notifications Section */}
          <div className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-4 sm:p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-[#434343] mb-4 flex items-center gap-2">
              <Bell className="h-5 w-5 text-[#7367FF]" />
              {t('settings.notifications.title')}
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#434343]">{t('settings.notifications.emailTitle')}</p>
                  <p className="text-xs text-[#434343]/60">{t('settings.notifications.emailDescription')}</p>
                </div>
                <Switch
                  checked={settings.emailNotifications}
                  onCheckedChange={() => handleSettingChange('emailNotifications')}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#434343]">{t('settings.notifications.pushTitle')}</p>
                  <p className="text-xs text-[#434343]/60">{t('settings.notifications.pushDescription')}</p>
                </div>
                <Switch
                  checked={settings.pushNotifications}
                  onCheckedChange={() => handleSettingChange('pushNotifications')}
                />
              </div>
            </div>
          </div>

          {/* Appearance Section */}
          <div className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-4 sm:p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-[#434343] mb-4 flex items-center gap-2">
              <Sun className="h-5 w-5 text-[#7367FF]" />
              {t('settings.appearance.title')}
            </h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Globe className="h-5 w-5 text-[#7367FF]" />
                  <div>
                    <p className="text-sm font-medium text-[#434343]">{t('settings.appearance.languageTitle')}</p>
                    <p className="text-xs text-[#434343]/60">{t('settings.appearance.languageDescription')}</p>
                  </div>
                </div>
                <LanguageOptions />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {settings.darkMode ? <Moon className="h-5 w-5 text-[#7367FF]" /> : <Sun className="h-5 w-5 text-[#7367FF]" />}
                  <div>
                    <p className="text-sm font-medium text-[#434343]">{t('settings.appearance.darkModeTitle')}</p>
                    <p className="text-xs text-[#434343]/60">{t('settings.appearance.darkModeDescription')}</p>
                  </div>
                </div>
                <Switch
                  checked={settings.darkMode}
                  onCheckedChange={() => {
                    handleSettingChange('darkMode');
                    toast.info(t('settings.toast.darkModeSoon'));
                  }}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Eye className="h-5 w-5 text-[#7367FF]" />
                  <div>
                    <p className="text-sm font-medium text-[#434343]">{t('settings.appearance.highContrastTitle')}</p>
                    <p className="text-xs text-[#434343]/60">{t('settings.appearance.highContrastDescription')}</p>
                  </div>
                </div>
                <Switch
                  checked={settings.highContrastMode}
                  onCheckedChange={() => handleSettingChange('highContrastMode')}
                />
              </div>
            </div>
          </div>

          {/* Audio & Playback Section */}
          <div className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-4 sm:p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-[#434343] mb-4 flex items-center gap-2">
              <Globe className="h-5 w-5 text-[#7367FF]" />
              {t('settings.audio.title')}
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#434343]">{t('settings.audio.autoPlayTitle')}</p>
                  <p className="text-xs text-[#434343]/60">{t('settings.audio.autoPlayDescription')}</p>
                </div>
                <Switch
                  checked={settings.autoPlayAudio}
                  onCheckedChange={() => handleSettingChange('autoPlayAudio')}
                />
              </div>
            </div>
          </div>

          {/* Privacy & Security Section */}
          <div className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-4 sm:p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-[#434343] mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#7367FF]" />
              {t('settings.privacy.title')}
            </h3>
            
            <div className="space-y-3">
              <Button
                variant="ghost"
                className="w-full justify-start text-[#434343] hover:bg-[#7367FF]/10"
                onClick={() => {
                  // Opens Clerk's security settings
                  if (user) {
                    toast.info(t('settings.toast.securityRedirect'));
                    // user.update() can be used for some settings
                  }
                }}
              >
                <Lock className="h-4 w-4 me-2" />
                {t('settings.privacy.changePassword')}
              </Button>
              
              <Button
                variant="ghost"
                className="w-full justify-start text-[#434343] hover:bg-[#7367FF]/10"
                onClick={handleExportData}
              >
                <Download className="h-4 w-4 me-2" />
                {t('settings.privacy.exportData')}
              </Button>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="bg-red-50/60 backdrop-blur-xl border border-red-200/40 rounded-2xl p-4 sm:p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-red-600 mb-4 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              {t('settings.danger.title')}
            </h3>
            
            <p className="text-sm text-red-600/80 mb-4">
              {t('settings.danger.description')}
            </p>
            
            {showDeleteConfirm ? (
              <div className="space-y-3">
                <p className="text-sm font-medium text-red-600">
                  {t('settings.danger.confirm')}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1 bg-red-500 hover:bg-red-600"
                    onClick={handleDeleteAccount}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <Loader2 className="h-4 w-4 animate-spin me-2" />
                    ) : null}
                    {t('settings.danger.confirmButton')}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-100"
                onClick={handleDeleteAccount}
              >
                <Trash2 className="h-4 w-4 me-2" />
                {t('settings.danger.deleteAccount')}
              </Button>
            )}
          </div>

          {/* Version Info */}
          <div className="text-center py-4">
            <p className="text-xs text-[#434343]/40">{t('common.version')}</p>
            <p className="text-xs text-[#434343]/40">{t('common.copyright')}</p>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}


